// src/pages/JoinReviewPage.js
//
// SourceX Roadmap PHASE 2, step 5 — the coordinator's review of the SoA x eCRF join.
//
// Roadmap §3: "the design must make the join VISIBLE and reviewable — 'SoA said
// X at Visit 1 → matched to eCRF form Y → confirm?' — rather than hidden. That
// reviewable-join step is how we responsibly hit 90% instead of overpromising
// 100%."  This page is that step, so its whole job is to be a REVIEW surface,
// never a blank page:
//
//  - Every row shows WHY it was matched (method, score, the matcher's or the
//    model's own words). A coordinator confirming a fuzzy match needs the
//    evidence, not a number.
//  - Rows needing attention sort FIRST — unmatched, then least-confident
//    proposals. Anything already confirmed sinks.
//  - "Not in the eCRF" is a first-class outcome, shown with the reason, because
//    some assessments genuinely live in IRT or on paper.
//
// Nothing here writes clinical data. It records which eCRF form answers which
// protocol assessment, which is why the Firestore rule gates writes on the same
// admin/coordinator check as template approval.

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { doc, getDoc, setDoc } from "firebase/firestore";
import { motion, AnimatePresence } from "framer-motion";
import {
  ArrowLeft,
  Check,
  X,
  Loader2,
  RefreshCw,
  Search,
  AlertTriangle,
  Link2,
  Plus,
  Sparkles,
  CheckCheck,
  FileStack,
  History,
  ChevronRight,
  ListChecks,
} from "lucide-react";
import { db } from "../firebase";
import { getSourcexTemplatesPath } from "../services/firestorePaths";
import { normalizeTemplate, stripAnswers, makeTemplateVersion } from "../services/templateSchema";
import {
  pushTemplateVersion,
  migrateEmbeddedVersions,
  listTemplateVersions,
} from "../services/templateVersionService";
import VersionHistoryModal from "../components/sourcex/VersionHistoryModal";
import StepProgressOverlay from "../components/sourcex/StepProgressOverlay";
import { useDialog } from "../components/sourcex/useDialog";
import { listenProtocols } from "../services/protocolExtractionService";
import { listenEcrfs } from "../services/ecrfExtractionService";
import { protocolApproved, needsReview } from "../services/sourcexWorkflow";
import { PROTOCOL_COMPARE_ENABLED } from "../config/featureFlags";
import { useSiteAdmin } from "../components/sourcex/useSiteAdmin";
import AiHelperNotice from "../components/AiHelperNotice";
import { logProtocolEvent } from "../services/protocolVersionService";
import {
  listenJoin,
  recomputeJoin,
  setMappingStatus,
  addManualMapping,
  confirmAllProposed,
  getEcrfFormOptions,
  assembleVisits,
} from "../services/joinService";
import {
  MATCH_METHODS,
  MATCH_STATUS,
  joinCoverage,
} from "../services/joinTypes";

const PageBackground = () => (
  <div className="fixed inset-0 -z-10 overflow-hidden">
    <img
      src="/bg_ai.jpg"
      alt=""
      aria-hidden="true"
      className="absolute inset-0 w-full h-full object-cover"
    />
    <div className="absolute inset-0 bg-gradient-to-br from-[#070418]/90 via-[#0c0726]/85 to-[#04030f]/95" />
    <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_rgba(124,58,237,0.25),_transparent_55%)]" />
  </div>
);

const card = "rounded-2xl border border-white/10 bg-white/[0.03] backdrop-blur-xl";

const METHOD_STYLE = {
  [MATCH_METHODS.EXACT]: "bg-emerald-400/10 border-emerald-400/30 text-emerald-200",
  [MATCH_METHODS.ALIAS]: "bg-emerald-400/10 border-emerald-400/30 text-emerald-200",
  [MATCH_METHODS.FUZZY]: "bg-sky-400/10 border-sky-400/30 text-sky-200",
  [MATCH_METHODS.LLM]: "bg-purple-400/10 border-purple-400/30 text-purple-200",
  [MATCH_METHODS.MANUAL]: "bg-amber-400/10 border-amber-400/30 text-amber-200",
};

const METHOD_LABEL = {
  [MATCH_METHODS.EXACT]: "exact name",
  [MATCH_METHODS.ALIAS]: "abbreviation",
  [MATCH_METHODS.FUZZY]: "similar name",
  [MATCH_METHODS.LLM]: "AI",
  [MATCH_METHODS.MANUAL]: "added by you",
};

const Pill = ({ className = "", children }) => (
  <span
    className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] font-medium ${className}`}
  >
    {children}
  </span>
);

const Stat = ({ label, value, tone = "text-slate-100" }) => (
  <div className={`${card} px-4 py-3`}>
    <div className="text-[11px] uppercase tracking-wide text-slate-400">{label}</div>
    <div className={`mt-0.5 text-2xl font-semibold ${tone}`}>{value}</div>
  </div>
);

const primaryBtn =
  "inline-flex items-center gap-1.5 px-4 py-2 rounded-xl bg-gradient-to-r from-emerald-500 to-teal-500 text-sm font-semibold text-white hover:brightness-110 disabled:opacity-50 disabled:cursor-not-allowed transition";
const dangerBtn =
  "inline-flex items-center gap-1.5 px-4 py-2 rounded-xl bg-gradient-to-r from-rose-600 to-red-600 text-sm font-semibold text-white hover:brightness-110 disabled:opacity-50 disabled:cursor-not-allowed transition";
const quietBtn =
  "inline-flex items-center gap-1.5 px-3.5 py-2 rounded-xl bg-white/[0.05] border border-white/12 text-sm text-slate-300 hover:bg-white/[0.1] hover:text-white transition";
// The small "open a modal" controls this page is made of.
const chipBtn =
  "inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border border-white/12 bg-white/[0.05] text-[11px] text-slate-300 hover:bg-white/[0.12] hover:text-white disabled:opacity-40 disabled:cursor-not-allowed transition";

/**
 * "12 Sep 2026, 14:05" from whatever the field happens to hold.
 *
 * Firestore serverTimestamps are NULL for the moment between the local write
 * and the server's acknowledgement, so a step that has just run has no time yet
 * and must not render "Invalid Date".
 */
const formatWhen = (value) => {
  if (!value) return "";
  const d = typeof value?.toDate === "function" ? value.toDate() : new Date(value);
  return Number.isNaN(d.getTime())
    ? ""
    : d.toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" });
};

/** Who last did a thing, and when. Both facts were already being written — the
 * join table stamps updatedAt/updatedBy on every save, and the assembled
 * template stamps assembledAt/assembledBy — and neither was ever shown. */
const StampLine = ({ verb, when, who, className = "" }) => {
  const at = formatWhen(when);
  if (!at && !who) return null;
  return (
    <span className={`text-[11px] text-slate-500 ${className}`}>
      {verb} {at || "just now"}
      {who && (
        <>
          {" by "}
          <span className="text-slate-400">{who}</span>
        </>
      )}
    </span>
  );
};

/** A centred dialog. Escape and the backdrop close it. Mirrors steps 1 and 2. */
function Modal({ onClose, title, children, footer, wide, tone }) {
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      role="dialog"
      aria-modal="true"
      className="fixed inset-0 z-[90] flex items-center justify-center bg-black/70 backdrop-blur-sm p-4"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <motion.div
        initial={{ y: 8, scale: 0.98 }}
        animate={{ y: 0, scale: 1 }}
        className={`w-full ${wide ? "max-w-3xl" : "max-w-md"} rounded-2xl border ${
          tone === "danger" ? "border-rose-400/40" : "border-white/12"
        } bg-[#0c0726] shadow-[0_24px_70px_-20px_rgba(0,0,0,0.9)] overflow-hidden`}
      >
        <div className="flex items-start gap-3 px-5 pt-5">
          <h2 className="min-w-0 flex-1 text-base font-semibold text-white leading-snug">
            {title}
          </h2>
          <button
            onClick={onClose}
            aria-label="Close"
            className="shrink-0 w-7 h-7 rounded-lg flex items-center justify-center text-slate-400 hover:text-white hover:bg-white/10 transition"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
        {children && (
          <div className="px-5 pt-2 pb-1 text-sm text-slate-300 max-h-[65vh] overflow-y-auto">
            {children}
          </div>
        )}
        <div className="flex flex-wrap items-center justify-end gap-2 px-5 py-4 mt-2 border-t border-white/10 bg-white/[0.02]">
          {footer}
        </div>
      </motion.div>
    </motion.div>
  );
}

/**
 * One protocol assessment and everything mapped to it. Grouped by SoA row
 * rather than listed per mapping, because the coordinator's question is "is
 * this assessment covered correctly?", not "is this individual link right?" —
 * and one assessment legitimately maps to several forms.
 */
const AssessmentRow = ({ group, formOptions, onConfirm, onReject, onAdd, busyKey }) => {
  const [adding, setAdding] = useState(false);
  const [pick, setPick] = useState("");

  const { soa_name: soaName, soa_key: soaKey, mappings, unmatched } = group;
  const live = mappings.filter((m) => m.status !== MATCH_STATUS.REJECTED);
  const allConfirmed = live.length > 0 && live.every((m) => m.status === MATCH_STATUS.CONFIRMED);

  return (
    <div
      className={`${card} p-4 ${
        allConfirmed ? "opacity-60" : ""
      } transition-opacity`}
    >
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="text-[11px] uppercase tracking-wide text-slate-400">
            Protocol says
          </div>
          <div className="text-base font-medium text-slate-100 break-words">{soaName}</div>
        </div>
        {allConfirmed && (
          <Pill className="bg-emerald-400/10 border-emerald-400/30 text-emerald-200">
            <Check size={12} /> confirmed
          </Pill>
        )}
      </div>

      {/* Nothing mapped: show why, and the shortlist so this is a choice and
          not a dead end. */}
      {!live.length && (
        <div className="mt-3 rounded-xl border border-amber-400/25 bg-amber-400/5 p-3">
          <div className="flex items-center gap-2 text-sm text-amber-200">
            <AlertTriangle size={14} />
            Not matched to any eCRF form
          </div>
          {unmatched?.reason && (
            <div className="mt-1 text-xs text-slate-300">{unmatched.reason}</div>
          )}
          {!!unmatched?.candidates?.length && (
            <div className="mt-2 flex flex-wrap gap-1.5">
              {unmatched.candidates.map((c) => (
                <button
                  key={c.form_id}
                  onClick={() => onAdd(soaKey, soaName, c.form_id, c.form_name)}
                  className="rounded-lg border border-white/15 bg-white/[0.04] px-2 py-1 text-xs text-slate-200 hover:bg-white/10"
                >
                  <Plus size={11} className="mr-1 inline" />
                  {c.form_name}
                  <span className="ml-1 text-slate-500">{c.score?.toFixed(2)}</span>
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Mapped forms. */}
      {live.map((m) => {
        const busy = busyKey === m.key;
        const confirmed = m.status === MATCH_STATUS.CONFIRMED;
        return (
          <div
            key={m.key}
            className="mt-3 rounded-xl border border-white/10 bg-white/[0.02] p-3"
          >
            <div className="flex flex-wrap items-start justify-between gap-2">
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 text-sm text-slate-100">
                  <Link2 size={13} className="shrink-0 text-slate-500" />
                  <span className="break-words">{m.form_name}</span>
                </div>
                <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
                  <Pill className={METHOD_STYLE[m.method] || "border-white/15 text-slate-300"}>
                    {m.method === MATCH_METHODS.LLM && <Sparkles size={10} />}
                    {METHOD_LABEL[m.method] || m.method}
                  </Pill>
                  {m.method !== MATCH_METHODS.MANUAL && (
                    <span className="text-[11px] text-slate-500">
                      confidence {Number(m.score || 0).toFixed(2)}
                    </span>
                  )}
                  {!!m.field_ids?.length && (
                    <span className="text-[11px] text-slate-500">
                      · {m.field_ids.length} specific field(s)
                    </span>
                  )}
                </div>
                {/* The evidence. Roadmap §3: the join must be visible, not hidden. */}
                {m.rationale && (
                  <div className="mt-1.5 text-xs leading-relaxed text-slate-400">
                    {m.rationale}
                  </div>
                )}
                {confirmed && m.reviewedBy && (
                  <div className="mt-1 text-[11px] text-emerald-300/70">
                    confirmed by {m.reviewedBy}
                  </div>
                )}
              </div>

              <div className="flex shrink-0 items-center gap-1.5">
                {busy ? (
                  <Loader2 size={16} className="animate-spin text-slate-400" />
                ) : (
                  <>
                    {!confirmed && (
                      <button
                        onClick={() => onConfirm(m.key)}
                        title="This mapping is correct"
                        className="rounded-lg border border-emerald-400/30 bg-emerald-400/10 p-1.5 text-emerald-200 hover:bg-emerald-400/20"
                      >
                        <Check size={14} />
                      </button>
                    )}
                    <button
                      onClick={() => onReject(m.key)}
                      title="This mapping is wrong"
                      className="rounded-lg border border-rose-400/30 bg-rose-400/10 p-1.5 text-rose-200 hover:bg-rose-400/20"
                    >
                      <X size={14} />
                    </button>
                  </>
                )}
              </div>
            </div>
          </div>
        );
      })}

      {/* Manual add — the escape hatch that makes a wrong match cheap to fix. */}
      {adding ? (
        <div className="mt-3 flex flex-wrap items-center gap-2">
          <select
            value={pick}
            onChange={(e) => setPick(e.target.value)}
            className="min-w-[16rem] flex-1 rounded-lg border border-white/15 bg-[#0c0726] px-2 py-1.5 text-sm text-slate-100"
          >
            <option value="">Choose an eCRF form…</option>
            {formOptions.map((f) => (
              <option key={f.form_id} value={f.form_id}>
                {f.name} ({f.fieldCount} fields)
              </option>
            ))}
          </select>
          <button
            disabled={!pick}
            onClick={() => {
              const f = formOptions.find((x) => x.form_id === pick);
              if (f) onAdd(soaKey, soaName, f.form_id, f.name);
              setPick("");
              setAdding(false);
            }}
            className="rounded-lg border border-emerald-400/30 bg-emerald-400/10 px-3 py-1.5 text-sm text-emerald-200 disabled:opacity-40"
          >
            Add
          </button>
          <button
            onClick={() => setAdding(false)}
            className="rounded-lg border border-white/15 px-3 py-1.5 text-sm text-slate-300"
          >
            Cancel
          </button>
        </div>
      ) : (
        <button
          onClick={() => setAdding(true)}
          className="mt-3 text-xs text-slate-400 hover:text-slate-200"
        >
          <Plus size={11} className="mr-1 inline" />
          map a different form
        </button>
      )}
    </div>
  );
};

export default function JoinReviewPage() {
  const { studyId } = useParams();
  // Once source documents are built, rebuilding is greyed out (edit them
  // instead); only a site admin can override, and it is recorded.
  const isSiteAdmin = useSiteAdmin();
  const navigate = useNavigate();

  const [table, setTable] = useState(null);
  const [loading, setLoading] = useState(true);
  const [computing, setComputing] = useState(false);
  const [busyKey, setBusyKey] = useState("");
  const [error, setError] = useState("");
  const [query, setQuery] = useState("");
  const [hideConfirmed, setHideConfirmed] = useState(false);
  const [formOptions, setFormOptions] = useState([]);
  const [assembling, setAssembling] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [assembledVersions, setAssembledVersions] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [restoringVersionId, setRestoringVersionId] = useState(null);
  // The built source documents, read only to answer "have they been built, when
  // and by whom?". Read-only: this page has never written that doc directly and
  // still does not.
  const [assembled, setAssembled] = useState(null);
  const [assembledChecked, setAssembledChecked] = useState(false);
  // Steps 1 and 2. null while the first snapshot is in flight, so the gate
  // below never says "do step 1 first" about a study whose protocols simply
  // have not arrived yet.
  const [protocols, setProtocols] = useState(null);
  const [ecrfs, setEcrfs] = useState(null);
  const [landingOpen, setLandingOpen] = useState(false);
  const [mappingOpen, setMappingOpen] = useState(false);
  const [warningsOpen, setWarningsOpen] = useState(false);
  const [rebuildOpen, setRebuildOpen] = useState(false);
  // Result of the assembly that just ran, so the "built" modal can hand the
  // SAME navigation state the page used to pass straight through.
  const [builtResult, setBuiltResult] = useState(null);
  // A long job that stopped, and which one — so the overlay can offer the right
  // retry without the user working out what failed.
  const [stalledError, setStalledError] = useState("");
  const [stalledAction, setStalledAction] = useState("");
  const { confirm: askConfirm, alert: showAlert, dialog } = useDialog();
  // The generated draft always lands at studyId__assembled (see assembleVisits
  // / assembleStudyVisits) — Version History here browses THAT doc's history,
  // same field the SectionEditor "Version History" button reads once you're
  // on the built-source-doc page.
  const assembledId = `${studyId}__assembled`;

  useEffect(() => {
    setLoading(true);
    const unsub = listenJoin(studyId, (t) => {
      setTable(t);
      setLoading(false);
    });
    return unsub;
  }, [studyId]);

  useEffect(() => {
    if (!table?.ecrf_id) return;
    getEcrfFormOptions(studyId, table.ecrf_id).then(setFormOptions).catch(() => {});
  }, [studyId, table?.ecrf_id]);

  // Steps 1 and 2, read only to answer "can this step even run yet?".
  // computeStudyJoin rejects with "This study has no protocol yet" / "no eCRF
  // yet" — better to say that before the click than after it. Failures are
  // swallowed: not knowing is a reason to offer less guidance, never a reason
  // to break step 3.
  useEffect(() => {
    if (!studyId) return undefined;
    return listenProtocols(studyId, setProtocols, () => setProtocols([]));
  }, [studyId]);

  useEffect(() => {
    if (!studyId) return undefined;
    return listenEcrfs(studyId, setEcrfs, () => setEcrfs([]));
  }, [studyId]);

  // Have the source documents already been built? Re-read after an assembly so
  // the page's own state agrees with what it just produced. Failures are
  // swallowed: not knowing is a reason to offer less guidance, never a reason
  // to break the mapping step.
  const refreshAssembled = useCallback(async () => {
    try {
      const snap = await getDoc(doc(db, getSourcexTemplatesPath(), assembledId));
      setAssembled(snap.exists() ? snap.data() : null);
    } catch (_) {
      /* treated as "not built" */
    } finally {
      setAssembledChecked(true);
    }
  }, [assembledId]);

  useEffect(() => {
    refreshAssembled();
  }, [refreshAssembled]);

  const hasAssembled = !!assembled;

  /**
   * The earliest step this study has NOT done, or null when step 3 can run.
   *
   * Step 1 counts as done only once its schedule of assessments is APPROVED —
   * see protocolApproved in services/sourcexWorkflow, which step 2 asks the
   * same question of. computeStudyJoin will fall back to the working extraction
   * if asked, but everything this step builds inherits that reading, so an
   * unreviewed one must not be what a study is mapped against.
   *
   * `null` lists mean "still loading" and block nothing — a gate must never
   * fire on data that has not arrived.
   */
  const blockedBy = useMemo(() => {
    if (protocols === null || ecrfs === null) return null;
    if (protocols.length === 0) {
      return {
        label: "Step 1 · Upload Protocol",
        why: "Upload the protocol first.",
        to: `/sourcex/protocols/${studyId}`,
      };
    }
    if (!protocolApproved(protocols)) {
      // Uploaded, not signed off. Point at the extraction waiting for review
      // where there is one, so this lands on the page that can clear the block
      // rather than on the upload screen they have already been through.
      const pending =
        protocols.find(needsReview) || protocols[protocols.length - 1];
      return {
        label: "Step 1 · Review & Approve Protocol",
        why: "The protocol's schedule of assessments has not been approved yet. Everything mapped here is built from it, so approve it first.",
        to: pending
          ? `/sourcex/protocols/${studyId}/${pending.id}`
          : `/sourcex/protocols/${studyId}`,
      };
    }
    if (ecrfs.length === 0) {
      return {
        label: "Step 2 · Upload Blank eCRF",
        why: "Upload the blank eCRF first.",
        to: `/sourcex/ecrf/${studyId}`,
      };
    }
    return null;
  }, [protocols, ecrfs, studyId]);

  // "You have already built these" — raised ONCE, on the first load that knows
  // enough to say so. A ref rather than state so re-renders cannot reopen a
  // modal the user has closed.
  const landingShown = useRef(false);
  useEffect(() => {
    if (landingShown.current || loading || !assembledChecked) return;
    landingShown.current = true;
    if (hasAssembled) setLandingOpen(true);
  }, [loading, assembledChecked, hasAssembled]);

  /**
   * @param {Function} fn
   * @param {""|"compute"|"assemble"} [stalls]  when this is one of the two long
   *   jobs, a failure also holds the full-screen overlay open in its stopped
   *   state with a retry — rather than dropping the user back on the page with
   *   a red line at the top of it. Everything else is fast enough that the
   *   banner is the right place.
   */
  const run = useCallback(async (fn, stalls = "") => {
    setError("");
    if (stalls) setStalledError("");
    try {
      await fn();
    } catch (e) {
      const message = (e && e.message) || String(e);
      setError(message);
      if (stalls) {
        setStalledError(message);
        setStalledAction(stalls);
      }
    }
  }, []);

  const onCompute = () =>
    run(async () => {
      setComputing(true);
      try {
        await recomputeJoin(studyId);
      } finally {
        setComputing(false);
      }
    }, "compute");

  const onStatus = (key, status) =>
    run(async () => {
      setBusyKey(key);
      try {
        await setMappingStatus(studyId, key, status);
      } finally {
        setBusyKey("");
      }
    });

  const onAdd = (soaKey, soaName, formId, formName) =>
    run(() => addManualMapping(studyId, { soaKey, soaName, formId, formName }));

  // Phase 3: assemble the per-visit source documents from this reviewed mapping.
  //
  // Unchanged server-side — same assembleVisits call, same navigation target,
  // same `state` payload the template editor reads. The page stops and says
  // what happened first: this is the step that rewrites the built documents,
  // and the sentence it ends on is the one telling the coordinator to go and
  // read them. From there the button lands exactly where it always did.
  const onAssemble = () =>
    run(async () => {
      setAssembling(true);
      try {
        const { templateId, stats, warningCount } = await assembleVisits(studyId);
        setBuiltResult({ templateId, stats, warningCount });
        await refreshAssembled();
      } finally {
        setAssembling(false);
      }
    }, "assemble");

  const openBuilt = (result) =>
    navigate(`/sourcex/template/${result?.templateId || assembledId}`, {
      state: result
        ? { assembledStats: result.stats, warningCount: result.warningCount }
        : undefined,
    });

  // "Generate source documents" (assembleStudyVisits, server-side) already
  // snapshots the PREVIOUS assembly into the versions subcollection before
  // overwriting it — this just reads that history back so it can be
  // browsed/restored without leaving the mapping page.
  const onOpenHistory = () => {
    setHistoryOpen(true);
    setHistoryLoading(true);
    run(async () => {
      try {
        setAssembledVersions(await listTemplateVersions(assembledId));
      } finally {
        setHistoryLoading(false);
      }
    });
  };

  const onRestoreAssembledVersion = (version) =>
    run(async () => {
      const ok = await askConfirm(
        `This replaces the built source documents with the version from ${new Date(
          version.createdAt
        ).toLocaleString()}. The current content is saved as a version first, so you can always come back to it.`,
        { title: "Restore this version?", confirmLabel: "Restore" }
      );
      if (!ok) return;

      setRestoringVersionId(version.id);
      try {
        const ref = doc(db, getSourcexTemplatesPath(), assembledId);
        const snap = await getDoc(ref);
        const latest = snap.exists()
          ? snap.data()
          : { studyId: assembledId, sourceStudyId: studyId, sections: [] };
        // A template opened before version history moved to its own
        // subcollection may still carry the old embedded array — migrate it
        // out and never let it ride along into the write below.
        await migrateEmbeddedVersions(assembledId, latest.versionHistory);
        delete latest.versionHistory;
        await pushTemplateVersion(
          assembledId,
          makeTemplateVersion({
            kind: "backup",
            label: "Before restoring an older version",
            sections: latest.sections || [],
          })
        );
        const restored = stripAnswers(
          normalizeTemplate({
            ...latest,
            sections: version.sections,
            status: "draft",
          })
        );
        await setDoc(ref, restored, { merge: true });
        setHistoryOpen(false);
        await showAlert("Restored. Review it, then approve again when you're ready.", {
          title: "✅ Restored",
        });
        navigate(`/sourcex/template/${assembledId}`);
      } finally {
        setRestoringVersionId(null);
      }
    });

  // Group by SoA assessment. One protocol row can map to several forms, and the
  // reviewer's unit of thought is the assessment, not the individual link.
  const groups = useMemo(() => {
    if (!table) return [];
    const byKey = new Map();
    const touch = (key, name) => {
      if (!byKey.has(key)) byKey.set(key, { soa_key: key, soa_name: name, mappings: [] });
      return byKey.get(key);
    };
    (table.mappings || []).forEach((m) => touch(m.soa_key, m.soa_name).mappings.push(m));
    (table.unmatched || []).forEach((u) => {
      touch(u.soa_key, u.soa_name).unmatched = u;
    });

    const list = [...byKey.values()];
    // Needs-attention first: nothing mapped, then least-confident proposals.
    // Confirmed work sinks, so the queue shortens as you go.
    const rank = (g) => {
      const live = g.mappings.filter((m) => m.status !== MATCH_STATUS.REJECTED);
      if (!live.length) return 0;
      if (live.every((m) => m.status === MATCH_STATUS.CONFIRMED)) return 2;
      return 1;
    };
    return list.sort((a, b) => {
      const ra = rank(a);
      const rb = rank(b);
      if (ra !== rb) return ra - rb;
      const sa = Math.min(...a.mappings.map((m) => m.score ?? 1), 1);
      const sb = Math.min(...b.mappings.map((m) => m.score ?? 1), 1);
      if (sa !== sb) return sa - sb;
      return String(a.soa_name).localeCompare(String(b.soa_name));
    });
  }, [table]);

  const visible = useMemo(() => {
    const q = query.trim().toLowerCase();
    return groups.filter((g) => {
      if (hideConfirmed) {
        const live = g.mappings.filter((m) => m.status !== MATCH_STATUS.REJECTED);
        if (live.length && live.every((m) => m.status === MATCH_STATUS.CONFIRMED)) return false;
      }
      if (!q) return true;
      return (
        String(g.soa_name).toLowerCase().includes(q) ||
        g.mappings.some((m) => String(m.form_name).toLowerCase().includes(q))
      );
    });
  }, [groups, query, hideConfirmed]);

  const cov = useMemo(() => joinCoverage(table, groups.length || undefined), [table, groups]);

  /**
   * Every complaint this mapping has, in one list.
   *
   * The fallback-model banner used to sit at the top of the page and the
   * matcher's own warnings were never shown at all. Neither blocks building the
   * documents and neither is a coordinator's job to fix, so they collapse behind
   * one button — the same shape steps 1 and 2 use. Nothing is dropped.
   */
  const allWarnings = useMemo(() => {
    if (!table) return [];
    const out = [];
    if ((table.degradedVendors || []).length > 0) {
      out.push({
        type: "fallback_model",
        message: `The preferred AI provider (${table.degradedVendors.join(
          ", "
        )}) was unreachable, so part of this mapping ran on a fallback model. Recompute once it is available.`,
        tone: "bad",
      });
    }
    (table.warnings || []).forEach((w) =>
      out.push(typeof w === "string" ? { type: "mapping", message: w } : w)
    );
    return out;
  }, [table]);

  return (
    <div className="sourcex-page min-h-screen text-slate-100">
      <PageBackground />

      {/* Both of this page's slow jobs, on one overlay — they never overlap, and
          which one is running is the only thing that differs.
            • Matching: ONE long call to computeStudyJoin, and the merged table
              is saved by THIS tab when it returns. Close the tab and the work
              the model just did is thrown away, so the rule is on.
            • Building: assembleStudyVisits writes the documents server-side, so
              it survives a closed tab — but this tab is also what navigates to
              the result, so the rule stays on for the far smaller reason that
              leaving means not being taken there.
          Neither reports progress, so the bar is honest about not knowing and
          the only number shown is elapsed time. */}
      <StepProgressOverlay
        open={computing || assembling || !!stalledError}
        title={computing ? "Matching protocol to eCRF" : "Building source documents"}
        detail={
          computing
            ? "Names first, then AI for the rest"
            : "One document per visit"
        }
        typical="usually a few minutes"
        keepTabOpen
        error={stalledError}
        retryLabel="Try again"
        onRetry={() => {
          setStalledError("");
          (stalledAction === "assemble" ? onAssemble : onCompute)();
        }}
        onDismiss={() => setStalledError("")}
      />
      <div className="mx-auto max-w-5xl px-4 py-8">
        <button
          onClick={() => navigate(-1)}
          className="mb-6 inline-flex items-center gap-1.5 px-3 py-2 rounded-xl bg-white/[0.04] border border-white/10 hover:bg-white/[0.08] text-xs text-slate-300 hover:text-white transition"
        >
          <ArrowLeft className="w-3.5 h-3.5" /> Back
        </button>

        <div className="mb-5">
          <div className="text-[10px] uppercase tracking-[0.22em] text-purple-300/80 font-semibold leading-none">
            Step 3 · Map &amp; Create Documents
          </div>
          <h1 className="mt-1 text-2xl font-semibold">{studyId}</h1>
          {PROTOCOL_COMPARE_ENABLED && (
            <AiHelperNotice compact className="mt-2">Some matches between the protocol and the eCRF are proposed by AI. Check them before building documents.</AiHelperNotice>
          )}
          {/* Who ran this step, and when. `updatedAt` / `updatedBy` were already
              stamped on every save of the join table; nothing showed them. */}
          {table && (
            <StampLine
              className="mt-1 block"
              verb="Mapping updated"
              when={table.updatedAt}
              who={table.updatedBy}
            />
          )}
        </div>

        {error && (
          <div className="mb-4 rounded-xl border border-rose-400/30 bg-rose-400/10 p-3 text-sm text-rose-200">
            {error}
          </div>
        )}

        {loading ? (
          <div className="flex items-center gap-2 text-slate-400">
            <Loader2 size={16} className="animate-spin" /> Loading…
          </div>
        ) : !table ? (
          // Nothing mapped yet: one thing to do, in the middle of the screen.
          // Green, and the same shape as step 1 and step 2's heroes.
          //
          // Dead until steps 1 and 2 are actually done. computeStudyJoin
          // rejects with "This study has no protocol yet" / "no eCRF yet", and
          // being told that AFTER clicking a big green button is a worse way to
          // learn it than the button saying so itself.
          <div className="flex flex-col items-center justify-center text-center py-20">
            {blockedBy ? (
              <>
                <button
                  disabled
                  title={`${blockedBy.label} first`}
                  className="inline-flex items-center justify-center px-12 py-7 rounded-3xl text-lg font-bold bg-white/[0.06] border border-white/10 text-slate-500 cursor-not-allowed"
                >
                  Build Mapping
                </button>
                <p className="mt-4 text-sm text-amber-200/90">{blockedBy.why}</p>
                <button
                  onClick={() => navigate(blockedBy.to)}
                  className="mt-3 inline-flex items-center gap-1.5 px-4 py-2 rounded-xl bg-gradient-to-r from-emerald-500 to-teal-500 text-sm font-semibold text-white hover:brightness-110 transition"
                >
                  Go to {blockedBy.label} <ChevronRight className="w-4 h-4" />
                </button>
              </>
            ) : (
              <button
                onClick={onCompute}
                disabled={computing}
                className={`inline-flex items-center justify-center gap-2 px-12 py-7 rounded-3xl text-lg font-bold text-white transition ${
                  computing
                    ? "bg-white/10"
                    : "bg-gradient-to-r from-emerald-500 to-teal-500 ring-2 ring-emerald-300/50 shadow-[0_18px_56px_-12px_rgba(16,185,129,0.8)] hover:shadow-[0_22px_68px_-10px_rgba(16,185,129,0.95)] hover:brightness-110"
                }`}
              >
                {computing && <Loader2 size={20} className="animate-spin" />}
                {computing ? "Working…" : "Build Mapping"}
              </button>
            )}
            <p className="mt-4 max-w-md text-xs text-slate-500">
              Needs an approved protocol SoA and a completed eCRF extraction.
              Matching runs on free string comparison first; only the rows it
              cannot decide are sent to AI.
            </p>
          </div>
        ) : (
          <>
            {/* The one thing on this page that is not optional reading. The
                mapping is AI-assisted and the roadmap is explicit that it aims
                at 90%, not 100% — so the built documents get checked by a human
                every time, and that has to be said where the building happens
                rather than left implied by a review list. */}
            <div className="mb-4 flex items-start gap-3 rounded-2xl border-2 border-amber-400/60 bg-amber-400/[0.14] ring-2 ring-amber-400/20 p-4 shadow-[0_10px_36px_-12px_rgba(251,191,36,0.5)]">
              <AlertTriangle
                size={24}
                className="mt-0.5 shrink-0 text-amber-300"
                aria-hidden="true"
              />
              <div className="min-w-0">
                <div className="text-base font-bold text-amber-100">
                  Check the built documents by hand.
                </div>
                <p className="mt-0.5 text-sm text-amber-100/85">
                  This mapping is AI-assisted and is <strong>not 100% reliable</strong>.
                  Read every generated source document before it is used.
                </p>
              </div>
            </div>

            {/* Rebuilding replaces work that may already have been edited and
                approved. Red, one line, and only once there is something to
                lose. The "last 20" is not a detail to leave out: the snapshot is
                real, but the versions subcollection is pruned to the newest 20
                whatever their status. */}
            {hasAssembled && (
              <div className="mb-4 flex items-start gap-2 rounded-xl border border-rose-400/40 bg-rose-500/[0.12] p-3 text-sm text-rose-100">
                <AlertTriangle size={16} className="mt-0.5 shrink-0 text-rose-300" />
                <span>
                  Source documents are already built — rebuilding replaces
                  everything in them (the current version is saved to Version
                  History, which keeps the last 20).
                </span>
              </div>
            )}

            {/* Everything this page knows, as buttons. The review list, the
                counts, the warnings and the cost are all still here and none of
                them was dropped — they are one click away instead of being the
                page, so what is left is the decision: open what was built, or
                rebuild it. */}
            {/* Two groups, because they are two different jobs. THIS MAPPING —
                read it, accept it, redo it — is the work of this page. The
                built documents' own history is about the thing this page
                produces, not about the mapping, and mixing the two put
                "Version history" in the middle of a row where every other
                button acted on the join table. */}
            <div className={`${card} mb-3 px-4 py-3`}>
              <div className="text-[10px] uppercase tracking-[0.18em] text-slate-500 font-semibold mb-2">
                This mapping
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <button onClick={() => setMappingOpen(true)} className={chipBtn}>
                  <ListChecks className="w-3.5 h-3.5" />
                  Show mapping ({cov.totalAssessments})
                </button>
                <button
                  onClick={() => run(() => confirmAllProposed(studyId))}
                  disabled={!table.mappings?.length}
                  className={chipBtn}
                >
                  <CheckCheck className="w-3.5 h-3.5" /> Confirm all
                </button>
                <button
                  onClick={onCompute}
                  disabled={computing || assembling}
                  className={chipBtn}
                >
                  {computing ? (
                    <Loader2 className="w-3.5 h-3.5 animate-spin" />
                  ) : (
                    <RefreshCw className="w-3.5 h-3.5" />
                  )}
                  {computing ? "Working…" : "Recompute"}
                </button>
                {allWarnings.length > 0 && (
                  <button
                    onClick={() => setWarningsOpen(true)}
                    className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border border-amber-400/25 bg-amber-400/[0.08] text-[11px] text-amber-200/90 hover:bg-amber-400/20 hover:text-white transition"
                  >
                    <AlertTriangle className="w-3.5 h-3.5" />
                    Show warnings ({allWarnings.length})
                  </button>
                )}
              </div>
            </div>

            <div className={`${card} mb-4 px-4 py-3`}>
              <div className="text-[10px] uppercase tracking-[0.18em] text-slate-500 font-semibold mb-2">
                Built source documents
              </div>
              <button onClick={onOpenHistory} className={chipBtn}>
                <History className="w-3.5 h-3.5" /> Version history
              </button>
            </div>

            {/* The decision. Once the documents exist, OPENING them is the loud
                option and rebuilding is the quiet, guarded one; before they
                exist, building them is the only thing to do. */}
            {hasAssembled ? (
              <div className="space-y-3">
                <motion.button
                  whileHover={{ y: -2 }}
                  whileTap={{ scale: 0.99 }}
                  onClick={() => openBuilt()}
                  className="w-full flex items-center gap-3 px-5 py-5 rounded-2xl bg-gradient-to-r from-emerald-500 to-teal-500 ring-2 ring-emerald-300/50 shadow-[0_14px_44px_-10px_rgba(16,185,129,0.75)] hover:shadow-[0_18px_56px_-8px_rgba(16,185,129,0.95)] hover:brightness-110 transition text-left"
                >
                  <span className="min-w-0 flex-1">
                    <span className="block text-lg font-bold text-white">
                      Open Built Source Documents
                    </span>
                    <span className="mt-0.5 block text-xs text-emerald-50/90">
                      Review them by hand — this is where you want to be.
                    </span>
                    {/* No "built by" line here on purpose. This button opens a
                        SET of visit-specific documents, each with its own
                        history and its own approver, so one assembly stamp
                        across all of them reads as a claim about every visit
                        that it does not make. Who ran an assembly, and when,
                        belongs where it is a fact about that ONE action: the
                        rebuild confirmation, and Version History. */}
                  </span>
                  <ChevronRight className="w-6 h-6 shrink-0 text-white" />
                </motion.button>
                {PROTOCOL_COMPARE_ENABLED && (
                  <div className="text-[12px] text-slate-400 text-center">
                    Source documents are already built, so edit those instead.
                    {!isSiteAdmin && " Only a site admin can rebuild them."}
                  </div>
                )}
                <button
                  onClick={() => setRebuildOpen(true)}
                  disabled={assembling || computing || !table.mappings?.length || (PROTOCOL_COMPARE_ENABLED && !isSiteAdmin)}
                  className="w-full inline-flex items-center justify-center gap-2 rounded-xl border border-rose-400/25 bg-rose-500/[0.06] px-4 py-2.5 text-sm text-rose-200/80 hover:bg-rose-500/15 hover:text-rose-100 disabled:opacity-40 disabled:cursor-not-allowed transition"
                >
                  {assembling ? (
                    <>
                      <Loader2 size={15} className="animate-spin" /> Rebuilding…
                    </>
                  ) : (
                    <>
                      <FileStack size={15} /> {PROTOCOL_COMPARE_ENABLED && isSiteAdmin ? "Rebuild anyway (admin)" : "Rebuild source documents from scratch"}
                    </>
                  )}
                </button>
              </div>
            ) : (
              !!table.mappings?.length && (
                <motion.button
                  whileHover={{ y: -2 }}
                  whileTap={{ scale: 0.99 }}
                  onClick={onAssemble}
                  disabled={assembling || computing}
                  className="w-full flex items-center gap-3 px-5 py-5 rounded-2xl bg-gradient-to-r from-emerald-500 to-teal-500 ring-2 ring-emerald-300/50 shadow-[0_14px_44px_-10px_rgba(16,185,129,0.75)] hover:shadow-[0_18px_56px_-8px_rgba(16,185,129,0.95)] hover:brightness-110 disabled:opacity-60 transition text-left"
                >
                  <span className="min-w-0 flex-1">
                    <span className="block text-lg font-bold text-white">
                      {assembling ? "Building…" : "Generate Source Documents"}
                    </span>
                    <span className="mt-0.5 block text-xs text-emerald-50/90">
                      Builds the per-visit documents from this mapping.
                    </span>
                  </span>
                  {assembling ? (
                    <Loader2 className="w-6 h-6 shrink-0 animate-spin text-white" />
                  ) : (
                    <ChevronRight className="w-6 h-6 shrink-0 text-white" />
                  )}
                </motion.button>
              )
            )}
          </>
        )}
      </div>

      <AnimatePresence>
        {/* Landed on step 3 with step 3 already done. Leads with the documents
            themselves; staying is a deliberate second choice, and the red line
            on the page behind this says what staying can cost. */}
        {landingOpen && (
          <Modal
            key="landing"
            onClose={() => setLandingOpen(false)}
            title="Source documents are already built."
            footer={
              <>
                <button onClick={() => setLandingOpen(false)} className={quietBtn}>
                  Stay on the mapping
                </button>
                <button
                  onClick={() => {
                    setLandingOpen(false);
                    openBuilt();
                  }}
                  className={primaryBtn}
                >
                  Open built source documents <ChevronRight className="w-4 h-4" />
                </button>
              </>
            }
          >
            {/* No "built by" line, for the same reason the button below has
                none: this is a SET of visit-specific documents, and one
                assembly stamp across all of them claims something about every
                visit that it does not support. */}
            <p>Read them by hand before use — the mapping behind them is AI-assisted.</p>
          </Modal>
        )}

        {/* The guard on the one destructive action this page has. Red, and it
            names what is replaced and what survives — see the warning banner. */}
        {rebuildOpen && (
          <Modal
            key="rebuild"
            tone="danger"
            onClose={() => setRebuildOpen(false)}
            title="Rebuild the source documents from scratch?"
            footer={
              <>
                <button onClick={() => setRebuildOpen(false)} className={quietBtn}>
                  Cancel
                </button>
                <button
                  onClick={() => {
                    setRebuildOpen(false);
                    if (PROTOCOL_COMPARE_ENABLED && hasAssembled) logProtocolEvent(studyId, { type: "rebuild_override", detail: "Map & Create: rebuilt from scratch" });
                    onAssemble();
                  }}
                  className={dangerBtn}
                >
                  <FileStack className="w-4 h-4" /> Rebuild
                </button>
              </>
            }
          >
            <p className="text-rose-100">
              Everything currently built is replaced, including any edits or
              approvals made since. The current version is saved to Version
              History first, which keeps the last 20.
            </p>
            <StampLine
              className="mt-2 block"
              verb="Currently built"
              when={assembled?.assembledAt}
              who={assembled?.assembledBy}
            />
          </Modal>
        )}

        {/* Step 3 is done. The page says so and then hands over the built
            documents — carrying the SAME navigation state the template editor
            has always been given, so nothing downstream sees a difference. */}
        {builtResult && (
          <Modal
            key="built"
            onClose={() => setBuiltResult(null)}
            title="Source documents built."
            footer={
              <>
                <button onClick={() => setBuiltResult(null)} className={quietBtn}>
                  Stay here
                </button>
                <button
                  onClick={() => {
                    const r = builtResult;
                    setBuiltResult(null);
                    openBuilt(r);
                  }}
                  className={primaryBtn}
                >
                  Open &amp; review them <ChevronRight className="w-4 h-4" />
                </button>
              </>
            }
          >
            <p>
              {builtResult.stats?.eventsBuilt != null
                ? `${builtResult.stats.eventsBuilt} visit${
                    builtResult.stats.eventsBuilt === 1 ? "" : "s"
                  } built.`
                : "Done."}{" "}
              Read every one before it is used — the mapping is AI-assisted.
            </p>
            {builtResult.warningCount > 0 && (
              <p className="mt-2 text-amber-200/90">
                {builtResult.warningCount} gap
                {builtResult.warningCount === 1 ? "" : "s"} flagged during the
                build — they are listed on the documents themselves.
              </p>
            )}
          </Modal>
        )}

        {/* The review surface. Same rows, same evidence, same controls it has
            always had — just not the whole page. */}
        {mappingOpen && table && (
          <Modal
            key="mapping"
            wide
            onClose={() => setMappingOpen(false)}
            title="Protocol → eCRF mapping"
            footer={
              <button onClick={() => setMappingOpen(false)} className={quietBtn}>
                Close
              </button>
            }
          >
            <p className="mb-3 text-xs text-slate-500">
              Each protocol assessment, and the eCRF form that captures it.
              Confirm what is right and correct what is not — this is what the
              per-visit source documents are built from.
            </p>

            <div className="mb-4 grid grid-cols-2 gap-3 sm:grid-cols-4">
              <Stat label="Assessments" value={cov.totalAssessments} />
              <Stat label="Confirmed" value={cov.confirmed} tone="text-emerald-300" />
              <Stat
                label="Needs review"
                value={cov.needsReview}
                tone={cov.needsReview ? "text-amber-300" : "text-slate-100"}
              />
              <Stat
                label="Not matched"
                value={cov.unmatched}
                tone={cov.unmatched ? "text-rose-300" : "text-slate-100"}
              />
            </div>

            <div className="mb-4 flex flex-wrap items-center gap-3">
              <div className="relative min-w-[14rem] flex-1">
                <Search
                  size={15}
                  className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-500"
                />
                <input
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Search assessments or forms…"
                  className="w-full rounded-xl border border-white/10 bg-white/[0.03] py-2 pl-9 pr-3 text-sm text-slate-100 placeholder:text-slate-500"
                />
              </div>
              <label className="flex items-center gap-2 text-sm text-slate-400">
                <input
                  type="checkbox"
                  checked={hideConfirmed}
                  onChange={(e) => setHideConfirmed(e.target.checked)}
                  className="accent-purple-400"
                />
                Hide confirmed
              </label>
            </div>

            <div className="space-y-3">
              {visible.map((g) => (
                <AssessmentRow
                  key={g.soa_key}
                  group={g}
                  formOptions={formOptions}
                  busyKey={busyKey}
                  onConfirm={(k) => onStatus(k, MATCH_STATUS.CONFIRMED)}
                  onReject={(k) => onStatus(k, MATCH_STATUS.REJECTED)}
                  onAdd={onAdd}
                />
              ))}
              {!visible.length && (
                <div className={`${card} p-8 text-center text-slate-400`}>
                  {hideConfirmed
                    ? "Everything has been reviewed."
                    : "Nothing matches that search."}
                </div>
              )}
            </div>

            {/* What the matching cost. Diagnostics for whoever tunes the
                pipeline, so it sits with the mapping it describes. */}
            {!!table.usage?.totalCalls && (
              <div className="mt-4 border-t border-white/10 pt-3 text-xs text-slate-500">
                AI cost for this mapping: {table.usage.totalCalls} calls ·{" "}
                {(table.usage.totalInputTokens || 0).toLocaleString()} in /{" "}
                {(table.usage.totalOutputTokens || 0).toLocaleString()} out · approx $
                {Number(table.usage.estimatedCostUsd || 0).toFixed(2)}
              </div>
            )}
          </Modal>
        )}

        {/* Every complaint the mapping has, on demand. */}
        {warningsOpen && (
          <Modal
            key="warnings"
            wide
            onClose={() => setWarningsOpen(false)}
            title={`${allWarnings.length} warning${allWarnings.length === 1 ? "" : "s"}`}
            footer={
              <button onClick={() => setWarningsOpen(false)} className={quietBtn}>
                Close
              </button>
            }
          >
            <p className="mb-3 text-xs text-slate-500">
              Notes from the matcher. None of them stops you building.
            </p>
            <ul className="space-y-2">
              {allWarnings.map((w, i) => (
                // Warnings carry no id and are display-only.
                // eslint-disable-next-line react/no-array-index-key
                <li
                  key={i}
                  className={`rounded-xl border px-3 py-2 text-xs ${
                    w.tone === "bad"
                      ? "border-rose-400/30 bg-rose-500/[0.08] text-rose-100/90"
                      : "border-amber-400/25 bg-amber-400/[0.07] text-amber-100/85"
                  }`}
                >
                  {w.type && (
                    <span
                      className={`mr-1.5 font-semibold ${
                        w.tone === "bad" ? "text-rose-300/80" : "text-amber-300/80"
                      }`}
                    >
                      [{w.type}]
                    </span>
                  )}
                  {w.message}
                </li>
              ))}
            </ul>
          </Modal>
        )}
      </AnimatePresence>

      {historyOpen && (
        <VersionHistoryModal
          // No eventId on purpose: this is step 3's own history — the set of
          // built documents as it stood before each rebuild replaced it — not
          // any one visit's.
          versions={assembledVersions}
          onRestore={onRestoreAssembledVersion}
          restoringId={restoringVersionId}
          loading={historyLoading}
          onClose={() => setHistoryOpen(false)}
        />
      )}
      {dialog}
    </div>
  );
}
